import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { setInstallationModeAPI } from '@/api/storeManagement/registration';
import { useAppContext } from '@/provider/contexts';
import { useRegistrationState } from '@/provider/registration/registrationContext';
import { SetInstallationModeRequest } from '@/types/pages/storeManagement/registration';
import { REGISTRATION_MODE, TAB_TYPE } from '@/types/pages/storeManagement/registration/registrationTypes';
import { useRegistrationLifecycleState } from '@/provider/registration/registrationLifecycleContext';

const TURN_STATUS_MAP = {
    [REGISTRATION_MODE.NORMAL_MODE]: 'on',
    [REGISTRATION_MODE.INSTALLATION_MODE]: 'off',
};

const MODE_SWITCH_TARGET_MAP = {
    [REGISTRATION_MODE.NORMAL_MODE]: REGISTRATION_MODE.INSTALLATION_MODE,
    [REGISTRATION_MODE.INSTALLATION_MODE]: REGISTRATION_MODE.NORMAL_MODE,
};

export const useRegistrationMode = () => {
    const { t } = useTranslation('common');
    const { manageStoreId, setNotification } = useAppContext();
    const { state, dispatch } = useRegistrationState();
    const { refreshRegistrationNow } = useRegistrationLifecycleState();
    const isInstallationMode = state.registrationMode === REGISTRATION_MODE.INSTALLATION_MODE;
    const isLoading = state.loadingState.storeStateLoading;
    const targetMode = MODE_SWITCH_TARGET_MAP[state.registrationMode];
    const statusWord = TURN_STATUS_MAP[state.registrationMode];

    const openConfirmDialog = useCallback(() => {
        dispatch({
            type: 'TOGGLE_DIALOG',
            payload: {
                dialog: 'isStoreStateOpen',
                isOpen: true,
            },
        });
    }, [dispatch]);

    const closeConfirmDialog = useCallback(() => {
        dispatch({
            type: 'TOGGLE_DIALOG',
            payload: {
                dialog: 'isStoreStateOpen',
                isOpen: false,
            },
        });
    }, [dispatch]);

    const confirmModeChange = useCallback(async () => {
        closeConfirmDialog();

        const postData: SetInstallationModeRequest = {
            store_id: manageStoreId as number,
            mode: targetMode,
        };

        const {
            data: { message, isSuccess },
        } = await setInstallationModeAPI(postData);

        if (!isSuccess) {
            setNotification({
                message,
                severity: 'error',
            });
            return;
        }

        dispatch({
            type: 'SET_STORE_STATUS',
            payload: targetMode,
        });

        setNotification({
            message,
            severity: 'success',
        });

        if (targetMode === REGISTRATION_MODE.INSTALLATION_MODE) {
            dispatch({
                type: 'SET_TAB',
                payload: TAB_TYPE.AP,
            });
        }
        await refreshRegistrationNow({
            resetTable: true,
        });
    }, [closeConfirmDialog, dispatch, manageStoreId, setNotification, targetMode, refreshRegistrationNow]);

    const confirmMessage = useMemo(() => {
        return t('Are you sure you want to turn {{status}} the installation mode?', {
            status: t(statusWord),
        });
    }, [statusWord, t]);

    return {
        state,
        isLoading,
        isInstallationMode,
        targetMode,
        confirmMessage,
        openConfirmDialog,
        closeConfirmDialog,
        confirmModeChange,
    };
};
